import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  onSnapshot,
  type Unsubscribe,
  Timestamp,
} from 'firebase/firestore'
import { getToken, onMessage } from 'firebase/messaging'
import { db, messaging } from './config'
import { COLLECTIONS } from '@/constants'
import type { ApiResponse } from '@/types'

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined

const getDb = () => {
  if (!db) throw new Error('Firebase is not configured.')
  return db
}

// ── Types ─────────────────────────────────────────────────────────────────────
export interface AppNotification {
  id: string
  engineerId: string
  type: 'task_assigned' | 'task_status' | 'admin_message'
  title: string
  body: string
  taskId?: string
  taskNumber?: string
  read: boolean
  createdAt: Date
}

// ── FCM token management ──────────────────────────────────────────────────────
export const notificationService = {
  /** Request browser permission + get FCM token, save to engineer doc */
  async registerToken(engineerId: string): Promise<string | null> {
    if (!messaging || !VAPID_KEY) return null
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') return null

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js',
          { scope: '/' }
        ),
      })

      if (token) {
        await updateDoc(doc(getDb(), COLLECTIONS.ENGINEERS, engineerId), {
          fcmToken: token,
          fcmTokenUpdatedAt: serverTimestamp(),
        })
      }
      return token
    } catch (err) {
      console.warn('[FCM] registerToken failed:', err)
      return null
    }
  },

  /** Set up foreground message listener — returns an unsubscribe fn */
  onForegroundMessage(callback: (payload: { title: string; body: string; data?: Record<string, string> }) => void): () => void {
    if (!messaging) return () => {}
    return onMessage(messaging, payload => {
      callback({
        title: payload.notification?.title ?? 'BSNL FTTH',
        body:  payload.notification?.body  ?? 'You have a new notification',
        data:  payload.data as Record<string, string> | undefined,
      })
    })
  },

  /** Write a notification doc for the engineer (in-app + triggers FCM via the SW) */
  async sendAssignmentNotification(params: {
    engineerId: string
    engineerName: string
    taskId: string
    taskNumber: string
    customerName: string
    taskType: string
    address: string
    priority: string
  }): Promise<ApiResponse<void>> {
    try {
      const title = `📋 New Task Assigned — ${params.taskNumber}`
      const body  =
        `Hi ${params.engineerName}, you have been assigned a ${params.taskType} task.\n` +
        `Customer: ${params.customerName}\n` +
        `Address: ${params.address}\n` +
        `Priority: ${params.priority.toUpperCase()}`

      await addDoc(collection(getDb(), COLLECTIONS.NOTIFICATIONS), {
        engineerId:   params.engineerId,
        type:         'task_assigned',
        title,
        body,
        taskId:       params.taskId,
        taskNumber:   params.taskNumber,
        read:         false,
        createdAt:    serverTimestamp(),
      })
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to send notification' }
    }
  },

  /** Real-time listener for an engineer's notifications */
  subscribeToNotifications(
    engineerId: string,
    callback: (notifications: AppNotification[]) => void
  ): Unsubscribe {
    const q = query(
      collection(getDb(), COLLECTIONS.NOTIFICATIONS),
      where('engineerId', '==', engineerId)
    )
    return onSnapshot(q, snap => {
      const items: AppNotification[] = snap.docs.map(d => ({
        id:          d.id,
        engineerId:  d.data().engineerId,
        type:        d.data().type,
        title:       d.data().title,
        body:        d.data().body,
        taskId:      d.data().taskId,
        taskNumber:  d.data().taskNumber,
        read:        d.data().read ?? false,
        createdAt:   (d.data().createdAt as Timestamp)?.toDate?.() ?? new Date(),
      }))
      items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      callback(items)
    })
  },

  /** Mark all of an engineer's notifications as read */
  async markAllRead(engineerId: string): Promise<void> {
    try {
      const q = query(
        collection(getDb(), COLLECTIONS.NOTIFICATIONS),
        where('engineerId', '==', engineerId),
        where('read', '==', false)
      )
      const snap = await getDocs(q)
      await Promise.all(snap.docs.map(d => updateDoc(d.ref, { read: true })))
    } catch (err) {
      console.warn('[Notifications] markAllRead failed:', err)
    }
  },

  /** Mark a single notification as read */
  async markRead(notifId: string): Promise<void> {
    try {
      await updateDoc(doc(getDb(), COLLECTIONS.NOTIFICATIONS, notifId), { read: true })
    } catch (err) {
      console.warn('[Notifications] markRead failed:', err)
    }
  },

  /** Notify all admins when an engineer tags @admin in a task comment */
  async sendCommentNotification(params: {
    engineerName: string
    taskId: string
    taskNumber: string
    commentText: string
  }): Promise<void> {
    try {
      const adminsSnap = await getDocs(
        query(collection(getDb(), COLLECTIONS.USERS), where('role', '==', 'admin'))
      )
      await Promise.all(
        adminsSnap.docs.map(d =>
          addDoc(collection(getDb(), COLLECTIONS.NOTIFICATIONS), {
            engineerId: d.id,
            type: 'admin_message',
            title: `💬 @admin tagged by ${params.engineerName}`,
            body: `On task ${params.taskNumber}: "${params.commentText.slice(0, 120)}${params.commentText.length > 120 ? '…' : ''}"`,
            taskId: params.taskId,
            taskNumber: params.taskNumber,
            read: false,
            createdAt: serverTimestamp(),
          })
        )
      )
    } catch (err) {
      console.warn('[Notifications] sendCommentNotification failed:', err)
    }
  },

  /** Notify all admins when an engineer submits feasibility for a task */
  async sendFeasibilitySubmittedNotification(params: {
    engineerName: string
    taskId: string
    taskNumber: string
    feasibilityStatus: 'feasible' | 'not_feasible'
  }): Promise<void> {
    try {
      const adminsSnap = await getDocs(
        query(collection(getDb(), COLLECTIONS.USERS), where('role', '==', 'admin'))
      )
      const statusText = params.feasibilityStatus === 'feasible' ? 'FEASIBLE' : 'NOT FEASIBLE'
      await Promise.all(
        adminsSnap.docs.map(d =>
          addDoc(collection(getDb(), COLLECTIONS.NOTIFICATIONS), {
            engineerId: d.id,
            type: 'admin_message',
            title: `⚡ Feasibility Submitted — ${params.taskNumber}`,
            body: `${params.engineerName} marked task as ${statusText} and requested approval.`,
            taskId: params.taskId,
            taskNumber: params.taskNumber,
            read: false,
            createdAt: serverTimestamp(),
          })
        )
      )
    } catch (err) {
      console.warn('[Notifications] sendFeasibilitySubmittedNotification failed:', err)
    }
  },
}

